import { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Stack,
  TextField,
  Button,
  Typography,
  MenuItem,
  Alert,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutline';
import { api } from '../api/client.js';
import { useRecaptcha } from '../components/Recaptcha.js';
import type { RecaptchaConfig } from '../lib/recaptcha.js';
import type { LandingCopy } from './landingCopy.js';

const TEAM_SIZES = ['1–10', '11–50', '51–200', '201–1000', '1000+'];

/**
 * The demo request form on the landing page.
 *
 * Five fields, two of them required. Every extra field is a reason somebody
 * closes the tab, and a name and an address are all that is needed to call
 * somebody back — the rest helps whoever picks it up prepare, and is optional
 * because of that.
 *
 * The submission always reports success, even when the server recognised it
 * as a duplicate. Telling a visitor "you already asked" would be both a worse
 * experience and a way of checking which addresses are in the table.
 */
export function DemoRequestForm({ copy, lang }: { copy: LandingCopy['demo']; lang: 'en' | 'tr' }) {
  const [form, setForm] = useState({ name: '', email: '', company: '', teamSize: '', message: '' });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recaptcha, setRecaptcha] = useState<RecaptchaConfig | null>(null);
  const captcha = useRecaptcha(recaptcha, 'demoRequest');

  useEffect(() => {
    api
      .get('/v1/meta')
      .then((r) => setRecaptcha((r.data as { recaptcha?: RecaptchaConfig }).recaptcha ?? null))
      .catch(() => setRecaptcha(null));
  }, []);

  const set = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post('/v1/public/demo-request', {
        ...form,
        locale: lang,
        // Where on the page they were, so whoever follows up knows what they
        // had just read.
        source: 'landing',
        recaptchaToken: await captcha.getToken(),
      });
      setSent(true);
    } catch (err) {
      setError(
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? copy.failed,
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box id="demo" sx={{ py: { xs: 8, md: 12 }, scrollMarginTop: 72 }}>
      <Container maxWidth="sm">
        <Typography
          variant="overline"
          sx={{ color: 'primary.main', fontWeight: 700, letterSpacing: 1.2, display: 'block', textAlign: 'center' }}
        >
          {copy.eyebrow}
        </Typography>
        <Typography sx={{ fontWeight: 800, fontSize: { xs: 26, md: 34 }, textAlign: 'center', mb: 1 }}>
          {copy.title}
        </Typography>
        <Typography color="text.secondary" sx={{ textAlign: 'center', mb: 4 }}>
          {copy.subtitle}
        </Typography>

        {sent ? (
          <Alert icon={<CheckCircleIcon />} severity="success" sx={{ justifyContent: 'center' }}>
            {copy.thanks}
          </Alert>
        ) : (
          <Box component="form" onSubmit={submit}>
            <Stack spacing={2}>
              <TextField
                label={copy.name}
                value={form.name}
                onChange={(e) => set('name', e.target.value)}
                required
                fullWidth
              />
              <TextField
                label={copy.email}
                type="email"
                value={form.email}
                onChange={(e) => set('email', e.target.value)}
                required
                fullWidth
              />
              <TextField
                label={copy.company}
                value={form.company}
                onChange={(e) => set('company', e.target.value)}
                fullWidth
              />
              <TextField
                select
                label={copy.teamSize}
                value={form.teamSize}
                onChange={(e) => set('teamSize', e.target.value)}
                fullWidth
              >
                <MenuItem value="">{copy.teamSizeAny}</MenuItem>
                {TEAM_SIZES.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label={copy.message}
                value={form.message}
                onChange={(e) => set('message', e.target.value)}
                multiline
                minRows={3}
                fullWidth
              />
            </Stack>

            {captcha.node}
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}

            <Button
              type="submit"
              size="large"
              variant="contained"
              fullWidth
              disabled={busy || !form.name.trim() || !form.email.trim()}
              sx={{ mt: 3, py: 1.3, fontSize: 16 }}
            >
              {busy ? copy.sending : copy.submit}
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, textAlign: 'center' }}>
              {copy.privacy}
            </Typography>
          </Box>
        )}
      </Container>
    </Box>
  );
}
